/**
 * Aligns "Move to District" bulk checkbox visibility with ShareModal's
 * District radio + ViewModal share button rules (same gates as opening Share and selecting District).
 */
import signUpState from '@edulastic/constants/const/signUpState'
import { roleuser, test as testConstants } from '@edulastic/constants'

const { statusConstants } = testConstants

/** Matches ShareModal connect() default when tests.maxSharingLevelAllowed is unset */
const SHARE_LEVEL = {
  INDIVIDUAL: 0,
  SCHOOL: 1,
  DISTRICT: 2,
  PUBLIC: 3,
  LINK: 4,
}

const DEFAULT_MAX_SHARING_LEVEL_ALLOWED = SHARE_LEVEL.LINK

/** Matches `sharedTypeMap` in Item.js: 1 → DISTRICT */
const DISTRICT_SHARED_TYPE_NUM = 1

/**
 * Test search / detail documents may expose district sharing as `sharedType`, `sharedTypes`, or flags.
 */
export function isTestSharedToDistrict(testItem) {
  if (!testItem) return false
  if (testItem.isDistrictLibrary || testItem.inDistrictLibrary) return true
  const st = testItem.sharedType
  if (st === 'DISTRICT' || st === 'district') return true
  if (st === DISTRICT_SHARED_TYPE_NUM || st === `${DISTRICT_SHARED_TYPE_NUM}`)
    return true
  const { sharedTypes } = testItem
  if (Array.isArray(sharedTypes)) {
    if (sharedTypes.some((x) => x === 'DISTRICT' || x === 'district'))
      return true
    if (
      sharedTypes.includes(DISTRICT_SHARED_TYPE_NUM) ||
      sharedTypes.includes(`${DISTRICT_SHARED_TYPE_NUM}`)
    )
      return true
  }
  return false
}

/**
 * @typedef {Object} DistrictShareCheckboxContext
 * @property {string} [userId]
 * @property {string} [userRole]
 * @property {object} [features]
 * @property {number} [userSignupStatus]
 * @property {number} [maxSharingLevelAllowed]
 * @property {boolean} [isCurator] org curator (getIsCurator)
 * @property {boolean} [isDemoPlaygroundUser]
 * @property {boolean} [hasPlaylistEditAccess] defaults true for tests (ShareModal prop)
 */

/**
 * User/session gates for showing the bulk district toolbar (ShareModal would still block District for these users).
 */
export function canShowDistrictBulkToolbar(ctx) {
  if (!ctx || ctx.isDemoPlaygroundUser) return false
  if (ctx.features?.isCurator || ctx.features?.isPublisherAuthor) return false
  if (ctx.userSignupStatus === signUpState.ACCESS_WITHOUT_SCHOOL) return false
  const max =
    ctx.maxSharingLevelAllowed != null
      ? ctx.maxSharingLevelAllowed
      : DEFAULT_MAX_SHARING_LEVEL_ALLOWED
  if (SHARE_LEVEL.DISTRICT > max) return false
  return true
}

/** ViewModal: share icon visibility */
export function isTestShareButtonVisibleForUser(testItem, ctx) {
  const { userId, userRole, isCurator } = ctx
  const authors = testItem.authors || []
  const derivedFromPremiumBankId = testItem.derivedFromPremiumBankId
  const owner = authors.some((o) => o._id === userId)
  const isEdulasticCurator = userRole === roleuser.EDULASTIC_CURATOR
  return (
    (owner || isCurator) && !isEdulasticCurator && !derivedFromPremiumBankId
  )
}

/**
 * For a published test: District radio enabled in ShareModal (inverse of RadioBtn disabled for DISTRICT).
 */
export function isDistrictShareRadioEnabledForPublishedTest(testItem, ctx) {
  const {
    features,
    userSignupStatus,
    maxSharingLevelAllowed,
    hasPlaylistEditAccess = true,
  } = ctx
  const max =
    maxSharingLevelAllowed != null
      ? maxSharingLevelAllowed
      : DEFAULT_MAX_SHARING_LEVEL_ALLOWED
  if (SHARE_LEVEL.DISTRICT > max) return false
  if (features?.isCurator || features?.isPublisherAuthor) return false
  if (!hasPlaylistEditAccess) return false
  if (userSignupStatus === signUpState.ACCESS_WITHOUT_SCHOOL) return false
  return true
}

/**
 * Single test: show bulk checkbox when user could open Share and use District for this test.
 */
export function canShowDistrictShareCheckboxForTest(testItem, ctx) {
  if (!ctx || ctx.isDemoPlaygroundUser) return false
  if (!ctx.isContentConvertEnabled) return false
  if (isTestSharedToDistrict(testItem)) return false
  if (testItem.status !== statusConstants.PUBLISHED) return false
  if (!isTestShareButtonVisibleForUser(testItem, ctx)) return false
  if (!isDistrictShareRadioEnabledForPublishedTest(testItem, ctx)) return false
  return true
}
